export const ROLES = {
  CUSTOMER: 'customer',
  DOCTOR: 'doctor',
  HOSPITAL_ADMIN: 'hospital_admin',
  ADMIN: 'admin',
  LAB_STAFF: 'lab_staff',
};

// Laboratory sub-roles (labs.LabStaffProfile.lab_role) and what each may reach.
export const LAB_ROLES = [
  { value: 'manager', label: 'Lab Manager', description: 'Full access, including users, settings and rate cards.' },
  { value: 'accounts', label: 'Accounts / Billing', description: 'Billing, receivables, payables, TPA and management reporting.' },
  { value: 'frontdesk', label: 'Front Office', description: 'Registration, bookings, billing counter, home collection and dispatch.' },
  { value: 'technician', label: 'Lab Technician', description: 'Worklist, results, QC, specimens, instruments and send-outs.' },
  { value: 'phlebotomist', label: 'Phlebotomist', description: 'Home-collection rounds and specimen handover only.' },
];

// The 25 lab permission keys, in the server's order (labs/permissions.py).
export const LAB_PERMISSIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'bookings', label: 'Appointments' },
  { key: 'patients', label: 'Patients' },
  { key: 'tests', label: 'Test catalogue' },
  { key: 'reports', label: 'Reports' },
  { key: 'billing', label: 'Billing' },
  { key: 'accounts', label: 'Accounts' },
  { key: 'tpa', label: 'TPA & insurance' },
  { key: 'clients', label: 'Clients & tie-ups' },
  { key: 'tariffs', label: 'Tariffs & rate cards' },
  { key: 'lab', label: 'Laboratory (specimens, QC, templates)' },
  { key: 'worklist', label: 'Worklist' },
  { key: 'inventory', label: 'Inventory & purchases' },
  { key: 'analytics', label: 'MIS & analytics' },
  { key: 'settings', label: 'Settings' },
  { key: 'homecollection', label: 'Home collection' },
  { key: 'dispatch', label: 'Report dispatch' },
  { key: 'sendouts', label: 'Send-outs' },
  { key: 'instruments', label: 'Instruments' },
  { key: 'dayclose', label: 'Day close' },
  { key: 'users', label: 'Users & access' },
  { key: 'billapproval', label: 'Bill approval' },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'workflows', label: 'Workflows' },
  { key: 'result_verify', label: 'Result verification' },
];

export const APPOINTMENT_STATUS = {
  BOOKED: 'booked',
  CONFIRMED: 'confirmed',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  NO_SHOW: 'no_show',
  RESCHEDULED: 'rescheduled',
};

export const STATUS_COLORS = {
  booked: 'warning',
  pending: 'warning',
  confirmed: 'info',
  completed: 'success',
  cancelled: 'error',
  no_show: 'default',
  rescheduled: 'secondary',
  active: 'success',
  inactive: 'default',
  suspended: 'error',
};

export const STATUS_BG = {
  booked: '#fff3e0',
  pending: '#fff3e0',
  confirmed: '#e3f2fd',
  completed: '#e8f5e9',
  cancelled: '#ffebee',
  no_show: '#f5f5f5',
  rescheduled: '#e0f2f1',
};

export const APPOINTMENT_SOURCES = [
  { value: 'direct', label: 'Direct Booking' },
  { value: 'referral', label: 'Doctor Referral' },
  { value: 'walk_in', label: 'Walk-In' },
  { value: 'online_search', label: 'Online Search' },
  { value: 'follow_up', label: 'Follow-Up' },
];

// How the booking reached the clinic. Mirrors Appointment.AppointmentChannel.
// Distinct from APPOINTMENT_SOURCES, which records *why* the patient came.
export const APPOINTMENT_CHANNELS = [
  { value: 'web', label: 'Website' },
  { value: 'mobile_app', label: 'Mobile App' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'phone', label: 'Phone Call' },
  { value: 'walk_in', label: 'Walk-In Counter' },
  { value: 'partner', label: 'Partner / Aggregator' },
];

// Mirrors Appointment.ServiceType.
export const SERVICE_TYPES = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'surgery', label: 'Surgery' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'other', label: 'Other' },
];

// Who settles the bill. Mirrors Appointment.PayerType.
export const PAYER_TYPES = [
  { value: 'self', label: 'Self Pay' },
  { value: 'company', label: 'Company' },
  { value: 'insurance', label: 'Insurance' },
];

// Chip colour per payer type, so the listings read at a glance.
export const PAYER_TYPE_META = {
  self: { label: 'Self Pay', color: 'default' },
  company: { label: 'Company', color: 'info' },
  insurance: { label: 'Insurance', color: 'secondary' },
};

export const SERVICE_TYPE_META = {
  consultation: { label: 'Consultation', color: 'primary' },
  surgery: { label: 'Surgery', color: 'error' },
  follow_up: { label: 'Follow Up', color: 'info' },
  other: { label: 'Other', color: 'default' },
};

// Gateways a clinic can bill through. Mirrors PaymentGatewayConfig.Provider.
export const PAYMENT_GATEWAY_PROVIDERS = [
  { value: 'razorpay', label: 'Razorpay' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'payu', label: 'PayU' },
  { value: 'cashfree', label: 'Cashfree' },
];

export const PAYMENT_GATEWAY_MODES = [
  { value: 'test', label: 'Test' },
  { value: 'live', label: 'Live' },
];

// Mirrors CustomerInsurance.PolicyType.
export const INSURANCE_POLICY_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'family_floater', label: 'Family Floater' },
  { value: 'group', label: 'Group / Corporate' },
  { value: 'government', label: 'Government Scheme' },
];

export const PAYMENT_METHODS = [
  { value: 'upi', label: 'UPI' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'debit_card', label: 'Debit Card' },
  { value: 'net_banking', label: 'Net Banking' },
  { value: 'wallet', label: 'Wallet' },
  { value: 'cash', label: 'Cash' },
  { value: 'company', label: 'Paid By Company' },
];

// Payment status -> MUI chip color + label. Mirrors Payment.PaymentStatus on the backend.
export const PAYMENT_STATUS_META = {
  successful: { label: 'Paid', color: 'success' },
  paid: { label: 'Paid', color: 'success' },
  pending: { label: 'Pending', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
  refunded: { label: 'Refunded', color: 'default' },
  // Unpaid reads red so outstanding money is obvious in a long listing.
  unpaid: { label: 'Unpaid', color: 'error' },
};

// Payment statuses a doctor may set from the listing (manual reconciliation).
export const PAYMENT_STATUS_OPTIONS = [
  { value: 'successful', label: 'Mark Paid' },
  { value: 'pending', label: 'Mark Pending' },
  { value: 'failed', label: 'Mark Failed' },
  { value: 'refunded', label: 'Mark Refunded' },
];

// A payment-received status means the appointment can be shown as "scheduled".
export const PAID_STATUSES = ['successful', 'paid'];

// Structured vitals captured on the prescription. `unit` is shown as an adornment;
// systolic/diastolic BP is a single free-text field by convention.
export const VITAL_FIELDS = [
  { key: 'blood_pressure', label: 'Blood Pressure', unit: 'mmHg', placeholder: '120/80', type: 'text' },
  { key: 'pulse', label: 'Pulse', unit: 'bpm', placeholder: '72', type: 'number' },
  { key: 'temperature', label: 'Temperature', unit: '°F', placeholder: '98.6', type: 'number' },
  { key: 'spo2', label: 'SpO₂', unit: '%', placeholder: '98', type: 'number' },
  { key: 'respiratory_rate', label: 'Respiratory Rate', unit: '/min', placeholder: '16', type: 'number' },
  { key: 'weight', label: 'Weight', unit: 'kg', placeholder: '70', type: 'number' },
  { key: 'height', label: 'Height', unit: 'cm', placeholder: '170', type: 'number' },
  { key: 'bmi', label: 'BMI', unit: 'kg/m²', placeholder: 'Auto', type: 'number', readOnly: true },
];

export const CONSULTATION_TYPES = [
  { value: 'in_person', label: 'In-Person' },
  { value: 'video', label: 'Video Consultation' },
  { value: 'both', label: 'Both' },
];

export const SPECIALIZATIONS = [
  'Allergy & Immunology',
  'Anesthesiology',
  'Cardiology',
  'Cardiothoracic Surgery',
  'Clinical Pathology',
  'Critical Care Medicine',
  'Dentistry',
  'Dermatology',
  'Diabetology',
  'Emergency Medicine',
  'Endocrinology',
  'ENT (Otorhinolaryngology)',
  'Family Medicine',
  'Gastroenterology',
  'General Medicine',
  'General Surgery',
  'Geriatrics',
  'Gynecology & Obstetrics',
  'Hematology',
  'Hepatology',
  'Infectious Disease',
  'Internal Medicine',
  'Interventional Radiology',
  'Neonatology',
  'Nephrology',
  'Neurology',
  'Neurosurgery',
  'Nuclear Medicine',
  'Oncology (Medical)',
  'Oncology (Surgical)',
  'Oncology (Radiation)',
  'Ophthalmology',
  'Oral & Maxillofacial Surgery',
  'Orthopedics',
  'Pain Management',
  'Palliative Care',
  'Pathology',
  'Pediatrics',
  'Pediatric Surgery',
  'Physical Medicine & Rehabilitation',
  'Plastic Surgery',
  'Podiatry',
  'Psychiatry',
  'Psychology',
  'Pulmonology',
  'Radiology',
  'Rheumatology',
  'Sleep Medicine',
  'Sports Medicine',
  'Transplant Surgery',
  'Urology',
  'Vascular Surgery',
];

export const HOSPITAL_FACILITIES = [
  '24/7 Emergency',
  'Accident & Trauma Center',
  'Ambulance Service',
  'Blood Bank',
  'Burn Unit',
  'Cafeteria',
  'Cardiac Catheterization Lab',
  'Chemotherapy Unit',
  'CT Scan',
  'Day Care / Short Stay',
  'Dental Clinic',
  'Dialysis Unit',
  'Diagnostic Lab',
  'ECG / EEG',
  'Endoscopy',
  'Eye Care Center',
  'Fertility Clinic (IVF)',
  'General Ward',
  'Hearing & Speech Therapy',
  'ICU (Intensive Care Unit)',
  'Laminar Flow OT',
  'Laparoscopic Surgery',
  'MRI',
  'NICU (Neonatal ICU)',
  'Nuclear Medicine',
  'Nutrition & Dietetics',
  'Operation Theatre (OT)',
  'Outpatient Department (OPD)',
  'Pathology Lab',
  'PET Scan',
  'Pharmacy (24/7)',
  'Physiotherapy',
  'Private / Deluxe Rooms',
  'Radiology / X-Ray',
  'Rehabilitation Center',
  'Robotic Surgery',
  'Semi-Private Rooms',
  'Teleconsultation',
  'Ultrasound / Sonography',
  'Vaccination Center',
  'Ventilator Support',
  'Wellness & Preventive Health',
  'Wheelchair Accessibility',
];

export const HOSPITAL_SERVICES = [
  'General Consultation',
  'Teleconsultation',
  'Health Check-up Packages',
  'Vaccination',
  'Blood Tests',
  'X-Ray',
  'Ultrasound',
  'ECG',
  'MRI',
  'CT Scan',
  'Physiotherapy',
  'Dental Care',
  'Eye Care',
  'Minor Surgery',
  'Day Care Surgery',
  'Maternity & Antenatal Care',
  'Dialysis',
  'Chemotherapy',
  'Home Sample Collection',
  'Home Nursing',
  'Ambulance',
  'Pharmacy Delivery',
  'Nutrition & Diet Counselling',
  'Mental Health Counselling',
];

export const HOSPITAL_ACCREDITATIONS = [
  // International
  'JCI (Joint Commission International)',
  'ACHSI (Australian Council on Healthcare Standards International)',
  'Accreditation Canada International',
  'ACHS (Australian Council on Healthcare Standards)',
  'DNV GL Healthcare',
  'EFQM (European Foundation for Quality Management)',
  'ISQua (International Society for Quality in Health Care)',
  'QHA Trent',
  'SQS (Swiss Association for Quality and Management Systems)',
  'Temos International',
  // India
  'NABH (National Accreditation Board for Hospitals)',
  'NABL (National Accreditation Board for Testing and Calibration Laboratories)',
  'NABH Entry Level',
  'NABH Pre-Entry Level',
  'NABH Blood Bank',
  'NABH Nursing Excellence',
  'NABH Emergency Department',
  'QCI (Quality Council of India)',
  // USA
  'TJC (The Joint Commission)',
  'AAAHC (Accreditation Association for Ambulatory Health Care)',
  'CARF International',
  'HFAP (Healthcare Facilities Accreditation Program)',
  'URAC',
  'NCQA (National Committee for Quality Assurance)',
  'COLA',
  'CAP (College of American Pathologists)',
  // UK
  'CQC (Care Quality Commission)',
  'CHKS',
  'HIS (Healthcare Improvement Scotland)',
  // Middle East
  'CBAHI (Saudi Central Board for Accreditation of Healthcare Institutions)',
  'DHA (Dubai Health Authority)',
  'HAAD (Health Authority Abu Dhabi)',
  // Others
  'ISO 9001:2015 (Quality Management)',
  'ISO 14001:2015 (Environmental Management)',
  'ISO 15189 (Medical Laboratories)',
  'ISO 15224 (Health Care Quality Management)',
  'KASH (Korea Association of Simulated Healthcare)',
  'COHSASA (Council for Health Service Accreditation of Southern Africa)',
  'MSQH (Malaysian Society for Quality in Health)',
  'HA (Hospital Accreditation – Thailand)',
  'KOIHA (Korea Institute for Healthcare Accreditation)',
  'JCQHC (Japan Council for Quality Health Care)',
  'Green OT Certification',
  'LEED Healthcare Certification',
];

export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const SLOT_DURATIONS = [
  { value: 15, label: '15 minutes' },
  { value: 20, label: '20 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '60 minutes' },
];

// Surgery types offered in the prescription form when "Surgery suggested" is checked.
export const SURGERY_TYPES = [
  'Appendectomy',
  'Bariatric Surgery',
  'Cataract Surgery',
  'Cardiac Bypass (CABG)',
  'Cesarean Section',
  'Cholecystectomy (Gallbladder Removal)',
  'Cosmetic / Plastic Surgery',
  'Dental Surgery',
  'Dialysis Access (AV Fistula)',
  'ENT Surgery',
  'Endoscopic Sinus Surgery',
  'Fracture Fixation / ORIF',
  'Gynecological Surgery',
  'Hernia Repair',
  'Hip Replacement',
  'Hysterectomy',
  'IVF / Reproductive Surgery',
  'Joint Arthroscopy',
  'Kidney Stone Removal (PCNL/URS)',
  'Knee Replacement',
  'Laparoscopic Surgery',
  'LASIK / Refractive Eye Surgery',
  'Mastectomy',
  'Neurosurgery',
  'Orthopedic Surgery',
  'Pediatric Surgery',
  'Prostate Surgery (TURP)',
  'Spine Surgery',
  'Thyroid Surgery',
  'Tonsillectomy',
  'Transplant Surgery',
  'Urological Surgery',
  'Vascular Surgery',
  'Other',
];

// Reusable communication templates surfaced in the follow-up workflow builder.
export const COMM_TEMPLATES = [
  {
    id: 'appointment_reminder',
    name: 'Appointment Reminder',
    channel: 'whatsapp',
    body:
      'Hi {{patient_name}}, this is a reminder for your appointment with Dr. {{doctor_name}} on {{appointment_date}} at {{appointment_time}}. Please reach 15 minutes early. — {{clinic_name}}',
  },
  {
    id: 'followup_due',
    name: 'Follow-up Due',
    channel: 'whatsapp',
    body:
      'Hi {{patient_name}}, your follow-up consultation with Dr. {{doctor_name}} is due on {{followup_date}}. Reply YES to confirm or call us to reschedule.',
  },
  {
    id: 'lab_results_ready',
    name: 'Lab Results Ready',
    channel: 'sms',
    body:
      'Hi {{patient_name}}, your lab results are ready and available in your patient portal. Please consult Dr. {{doctor_name}} for next steps.',
  },
  {
    id: 'prescription_dispatched',
    name: 'Prescription Dispatched',
    channel: 'whatsapp',
    body:
      'Hi {{patient_name}}, your prescription has been shared. Please find it attached or download it from your portal. Wishing you a speedy recovery.',
  },
  {
    id: 'surgery_counseling',
    name: 'Surgery Counseling Invite',
    channel: 'whatsapp',
    body:
      'Hi {{patient_name}}, Dr. {{doctor_name}} has recommended you attend a counseling session for {{surgery_type}}. Please reach out to schedule a convenient time.',
  },
  {
    id: 'post_op_check',
    name: 'Post-Op Check-in',
    channel: 'whatsapp',
    body:
      'Hi {{patient_name}}, how are you feeling after the procedure? Reply with any concerns or questions — your care team is here to help.',
  },
  {
    id: 'payment_reminder',
    name: 'Payment Reminder',
    channel: 'sms',
    body:
      'Hi {{patient_name}}, a payment of {{amount}} is pending against your visit on {{visit_date}}. Pay via {{payment_link}} or visit the clinic reception.',
  },
  {
    id: 'thank_you',
    name: 'Thank You Note',
    channel: 'email',
    body:
      'Dear {{patient_name}},\n\nThank you for choosing {{clinic_name}}. We hope your visit met your expectations. If you have a moment, please leave us a review.\n\nWarm regards,\n{{clinic_name}}',
  },
];

// Clinic service master — used for booking & billing dropdowns.
export const CLINIC_SERVICE_TYPES = [
  { value: 'physical', label: 'Physical Consultation' },
  { value: 'tele', label: 'Tele Consultation' },
  { value: 'video', label: 'Video Consultation' },
  { value: 'surgery', label: 'Surgery' },
  { value: 'follow_up', label: 'Follow-Up' },
];

// Extended appointment statuses that include the counseling workflow.
export const EXTENDED_APPOINTMENT_STATUS = {
  ...APPOINTMENT_STATUS,
  COUNSELING_PENDING: 'counseling_pending',
  COUNSELING_IN_PROGRESS: 'counseling_in_progress',
  COUNSELING_COMPLETED: 'counseling_completed',
};
